/* eslint-disable no-unsafe-optional-chaining */
import React, { useState, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import cn from 'classnames'
// Components
import HeadSeo from '@/components/HeadSeo'
import CitiesList from '@/components/landings/CitiesList'
import Footer from '@/components/newFooter'
import Button from '@/components/newButton/Button'
import OurPartners from '@/components/ourPartners'
// Utils
import {
  breedNameTextConverter,
  capitalizeFirstLetterOfTitle,
  slugReverter,
  stateMap,
  initializeApollo,
  useMediaQuery,
} from '@utils'
import cities from '@/utils/us-cities.json'
import { getMetaTagsData, getPageIndex } from '@/components/landings/util'

import { useRelatedBreeds, useFavourites } from '@hooks'
import { DogsLandingsContext } from '@context/dogsLandingsContext'

import styles from './index.module.scss'
// GQL Queries
import {
  GET_FRONTEND_CONFIG_LANDING,
  GET_CATEGORIES,
  GET_STATE_LANDING,
  GET_LANDINGS_BREED_DETAILS,
  PUPPY_SEARCH,
  BREED_LIST_BY_STATE,
  STATE_LIST_BY_BREED,
  GET_SEO_INDEX_RECORDS,
} from '@graphql'
import { Breed, BreedCityLandingProps, GetSeoIndexRecordsResponse } from '@/types'

// Dynamic
const CustomerReview = dynamic(() => import('@/components/search/CustomerReview'))
const Featured = dynamic(() => import('@/components/landings/Featured'))
const LandingCover = dynamic(() => import('@/components/landings/LandingCover'))
const How = dynamic(() => import('@/components/home/How'))
const Why = dynamic(() => import('@/components//home/Why'))
const Facts = dynamic(() => import('@/components/landings/Facts'))
const RelatedBreeds = dynamic(() => import('@/components/landings/RelatedBreeds'))
const Categories = dynamic(() => import('@/components/home/Categories'))
const NationWide = dynamic(() => import('@/components/landings/NationWide'))
const RelatedBreedsDogs = dynamic(() => import('@/components/search/RelatedBreeds'))
const StateHistory = dynamic(() => import('@/components/landings/StateHistory'))
const PuppyQuizBanner = dynamic(() => import('@/components/search/PuppyQuizBanner'))
const SignupModal = dynamic(() => import('@/components/signupModal'))
const Header = dynamic(() => import('@/components/newHeader/Header'))
const BreedsList = dynamic(() => import('@/components/landings/BreedList'))

const ROOT_URL = process?.env?.NEXT_PUBLIC_ROOT_URL

// Module-level cache for static data that rarely changes
let cachedSeoIndexRecords: any = null

export default function BreedCityLanding({
  metaTags,
  breedsByState,
  statesByBreed,
  nationwidePuppies,
  allBreedPuppies,
  breedDetails,
  statesData,
  categoriesData,
  variables,
  landingType,
  citySlug,
}: BreedCityLandingProps) {
  const [showSignup, setShowSignup] = useState(false)

  const isMobile = useMediaQuery('(max-width:600px)')
  const router = useRouter()
  const breed = breedDetails?.breed || {}

  const { relatedBreeds } = useRelatedBreeds(breed?.id)
  const { favourites, selectedDog, toggleFavourite, refreshUserData } = useFavourites()

  const nationWideDogs = useMemo(() => nationwidePuppies?.search?.rows || [], [nationwidePuppies])

  const breedPuppies = useMemo(() => {
    const puppies = allBreedPuppies?.search?.rows.filter((elem) => {
      return elem.breeder.state === variables?.state
    })

    // Sort puppies by distance
    puppies.sort((a, b) => a.distance - b.distance)

    return puppies
  }, [allBreedPuppies, variables?.state])

  const dogs = breedPuppies || []
  // handle state not found or timeout error
  const stateFacts =
    statesData?.state && statesData?.state?.length && statesData.state[0]
      ? statesData.state[0]
      : undefined

  // Memoize the relatedBreedsDogs array
  const relatedBreedsDogs = useMemo(() => {
    return relatedBreeds
      .map((breed) => ({
        id: breed?.id,
        name: breed?.name,
        slug: breed?.slug,
        dogs: [...breed?.dogs].filter((dog) =>
          [...variables?.borderingStates, variables?.state]?.includes(dog?.breeder?.state)
        ),
        dogsAvailable: [...breed?.dogs].filter((dog) =>
          [...variables?.borderingStates, variables?.state]?.includes(dog?.breeder?.state)
        ).length,
      }))
      .filter((breed) => breed?.dogs?.length > 0)
  }, [relatedBreeds, variables?.borderingStates, variables?.state])

  // Filter the breeds that doesn't have any dogs
  const filteredBreedDogs = relatedBreedsDogs?.filter((breed) => breed?.dogs?.length > 0)

  const handleUpdateFavourite = (id) => {
    const success = toggleFavourite(id)
    if (!success) {
      setShowSignup(true)
    }
  }

  return (
    <DogsLandingsContext.Provider value={{ variables, landingType }}>
      <HeadSeo metaTags={metaTags} />
      <SignupModal
        isOpen={showSignup}
        close={() => setShowSignup(false)}
        success={() => {
          setShowSignup(false)
          refreshUserData()
          toggleFavourite(selectedDog)
        }}
      />
      <div className={styles.main}>
        <Header />

        <LandingCover
          breed={breed}
          variables={variables}
          landingType={landingType}
          city={capitalizeFirstLetterOfTitle(slugReverter(citySlug))}
        />

        <Featured
          title={`Our ${breedNameTextConverter(
            breed?.name
          )} Puppies in ${capitalizeFirstLetterOfTitle(
            slugReverter(citySlug)
          )}, ${variables.state}`}
          dogs={dogs}
          variables={variables}
          breed={breed}
          city={capitalizeFirstLetterOfTitle(slugReverter(citySlug))}
        />

        <NationWide
          title={` ${breedNameTextConverter(breed?.name)} Puppies Nationwide`}
          allDogs={nationWideDogs}
          slug={breed?.slug}
          hasBreeds
        />

        {filteredBreedDogs.length !== 0 && (
          <div
            className={cn(styles._sectionRbdogs, styles.relatedBreedsWrapper)}
            id="relatedDogBreeds"
          >
            <RelatedBreedsDogs
              title="Related Breeds Nearby"
              relatedBreeds={filteredBreedDogs}
              favourites={favourites}
              updateFavourite={handleUpdateFavourite}
              state
            />
            <div className={styles.featuredButton}>
              <Button
                title={`Explore puppies in ${variables?.stateName}`}
                theme={['green-solid', 'medium']}
                rightImage="/next-white.svg"
                onClick={() => router.push(`/puppies-for-sale/${variables?.stateSlug}`)}
              />
            </div>
          </div>
        )}

        <div className={styles._sectionRw}>
          {isMobile && <hr />}
          <CustomerReview />
        </div>

        <How />
        <Why />

        <div className={styles._sectionQuiz}>
          <OurPartners />
          <PuppyQuizBanner />
        </div>

        <Facts breed={breed} />

        <div id="relatedBreeds" className={styles._sectionRb}>
          <RelatedBreeds relatedBreeds={relatedBreeds} breed={breed} />
        </div>
        <div className={styles._sectionCategories}>
          <Categories
            title="Explore dog categories nationwide"
            categories={Object.keys(categoriesData)}
          />
        </div>

        <StateHistory state={stateFacts} variables={variables} />

        <div className={styles.listWrapper}>
          <CitiesList
            type="breed-state"
            title={`Find ${breed.name}s in Nearby Cities`}
            breedName={breed?.slug}
            more="View all cities"
            less="View less cities"
          />
          <BreedsList
            type="byLocation"
            more="View all locations"
            less="View less locations"
            title={`Find ${breed?.name}s in Other States`}
            breedSlug={breed?.slug}
            breedsData={statesByBreed}
            stateName={variables?.stateName}
            stateSlug={variables?.stateSlug}
            withDogs={statesByBreed?.withDogs}
            withoutDogs={statesByBreed?.withoutDogs}
          />
          <BreedsList
            type="inState"
            more="View all breeds"
            less="View less breeds"
            title={`Find Other Breeds in ${variables?.stateName}`}
            breedSlug={breed?.slug}
            breedsData={breedsByState}
            stateName={variables?.stateName}
            stateSlug={variables?.stateSlug}
            withDogs={breedsByState?.withDogs}
            withoutDogs={breedsByState?.withoutDogs}
          />
        </div>

        <Footer />
      </div>
    </DogsLandingsContext.Provider>
  )
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

// Redirection map
const redirects = {
  'weimaraner-1': 'weimaraner',
  cavadoodles: 'cavapoo',
  'golden-bernedoodle': 'golden-mountain-doodle',
  'central-asian-shepherd-dog-1': 'central-asian-shepherd-dog',
  'papi-tzu': 'papastzu',
  'Yorkie-Bichon-Mix': 'yorkie-bichon-mix',
  poodle: 'standard-poodle',
}

export async function getStaticProps({ params }) {
  const apolloClient = initializeApollo()
  const slug = params['breed-slug']
  const stateSlug = params?.state
  const citySlug = params?.city

  let variables
  let landingType
  let seoIndexRecords

  let categoriesData = {}
  let breedDetails: { breed: Breed | null } = {
    breed: null,
  }
  let statesData = {}
  let allBreedPuppies
  let nationwidePuppies

  let breedsByState = {
    withDogs: [],
    withoutDogs: [],
    breedListByState: [],
  }
  let statesByBreed = {
    withDogs: [],
    withoutDogs: [],
    stateListByBreed: [],
  }

  if (slug && stateSlug && citySlug) {
    // Check if the breed slug is in the redirection map
    landingType = 'breedCity'

    const city = cities.find((city) => city.slug === citySlug)
    if (!city) return { notFound: true }

    if (Object.prototype.hasOwnProperty.call(stateMap, stateSlug)) {
      variables = {
        // breed slug
        slug,
        // state data
        state: stateMap[stateSlug].state,
        stateName: stateMap[stateSlug].name,
        country: 'USA',
        stateSlug,
        borderingStates: stateMap[stateSlug].borderingState,
        cityZipcode: city['zip code'],
        citySlug,
      }
    } else {
      if (stateSlug === 'samoa') {
        return {
          redirect: {
            destination: `/puppies-for-sale/${slug}/american-samoa`,
            statusCode: 301,
          },
        }
      } else {
        return {
          notFound: true,
        }
      }
    }

    // Check for redirects
    if (redirects[slug]) {
      return {
        redirect: {
          destination: `/puppies-for-sale/${redirects[slug]}/${stateSlug}`,
          statusCode: 301,
        },
      }
    }
  }

  try {
    // First fetch breed details since frontendConfig depends on it
    const { data } = await apolloClient.query({
      query: GET_LANDINGS_BREED_DETAILS,
      variables: {
        slug: variables?.slug,
      },
    })

    breedDetails = data

    // Parallelize remaining independent queries for better performance
    const [
      {
        data: { frontendConfig: _landingPhotos },
      },
      { data: _allPuppies },
      {
        data: { categories },
      },
      { data: _stateData },
      { data: breedsByStateList },
      { data: statesByBreedList },
    ] = await Promise.all([
      apolloClient.query({
        query: GET_FRONTEND_CONFIG_LANDING,
        variables: {
          key: `breed-${data?.breed?.id}`,
        },
      }),
      apolloClient.query({
        query: PUPPY_SEARCH,
        variables: {
          breedSlug: variables?.slug,
          limit: 5,
          page: 1,
        },
      }),
      apolloClient.query({ query: GET_CATEGORIES }),
      apolloClient.query({
        query: GET_STATE_LANDING,
        variables: { code: variables?.state },
      }),
      apolloClient.query({
        query: BREED_LIST_BY_STATE,
        variables: { state: variables?.state },
      }),
      apolloClient.query({
        query: STATE_LIST_BY_BREED,
        variables: { slug: variables?.slug },
      }),
    ])

    const landingPhotos = _landingPhotos?.value
    nationwidePuppies = _allPuppies
    categoriesData = categories
    statesData = _stateData
    breedsByState = breedsByStateList
    statesByBreed = statesByBreedList

    // Finally fetch breed puppies with frontendConfigData
    const { data: _breedPuppies } = await apolloClient.query({
      query: PUPPY_SEARCH,
      variables: {
        breedSlug: variables?.slug,
        state: variables?.state,
        sort: 'by-view-score',
        frontendConfigData: landingPhotos || '[]',
        limit: 20,
        zipcode: variables?.cityZipcode.toString() || '',
      },
    })

    allBreedPuppies = _breedPuppies
  } catch (error) {
    console.error('BREED + STATE LANDING ERROR', error)
  }

  if (breedDetails?.breed === null) {
    return {
      notFound: true,
    }
  }

  // SEO Index Records (cached at module level)
  if (!cachedSeoIndexRecords) {
    try {
      const { data: seoIndexRecordsData = { getSeoIndexRecords: [] } } = (await apolloClient.query({
        query: GET_SEO_INDEX_RECORDS,
      })) as { data: GetSeoIndexRecordsResponse }

      cachedSeoIndexRecords = seoIndexRecordsData?.getSeoIndexRecords || []
    } catch (error) {
      console.error('SEO INDEX RECORDS ERROR', error)
      cachedSeoIndexRecords = []
    }
  }
  seoIndexRecords = cachedSeoIndexRecords

  const { robots = 'index, follow', canonical = '' } = getPageIndex(
    breedDetails?.breed || null,
    variables,
    'breedState',
    seoIndexRecords
  )

  const {
    title = '',
    description = '',
    url = '',
    image = '',
    breadCrumbSD = '',
    imageObj = '',
  } = getMetaTagsData(breedDetails?.breed || null, variables, landingType)

  return {
    props: {
      metaTags: {
        title,
        description,
        url,
        image,
        breadCrumbSD,
        imageObj,
        robots: ROOT_URL === 'https://www.mawoopets.com' ? robots : 'noindex, nofollow',
        canonical: canonical || url,
      },
      breedsByState: breedsByState?.breedListByState || {},
      statesByBreed: statesByBreed?.stateListByBreed || {},
      allBreedPuppies: allBreedPuppies || {},
      nationwidePuppies: nationwidePuppies || {},
      breedDetails: breedDetails || {},
      categoriesData: categoriesData || {},
      statesData: statesData || {},
      landingType: landingType || '',
      variables: variables || {},
      citySlug: citySlug || '',
    },
    revalidate: 60 * 60 * 24 * 30, // Revalidate every 30 days
  }
}
